import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Star, Play, Plus } from "lucide-react";
import HeroCarousel from "../components/HeroCarousel";
import Reveal from "../components/Reveal";
import BlurImage from "../components/BlurImage";
import { Icon } from "../lib/icons";
import api from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Testimonials } from "./Launch";

function useContent() {
    const [data, setData] = useState({ services: [], portfolio: [], courses: [], journey: [], artists: [], tests: [] });
    useEffect(() => {
        Promise.all([
            api.get("/services"), api.get("/portfolio"), api.get("/courses"), api.get("/journey"), api.get("/artists"), api.get("/testimonials"),
        ]).then(([s, p, c, j, a, t]) =>
            setData({ services: s.data, portfolio: p.data, courses: c.data, journey: j.data, artists: a.data, tests: t.data })
        ).catch(() => {});
    }, []);
    return data;
}

export default function Home() {
    const { services, portfolio, courses, journey, artists, tests } = useContent();
    const { openLead, openWhatsApp, openNotify } = useSite();

    return (
        <div data-testid="home-page">
            <HeroCarousel />

            {/* Services row */}
            <section className="py-16 lg:py-24 bg-white" data-testid="home-services">
                <div className="mx-container">
                    <div>
                        <div className="text-center max-w-2xl mx-auto mb-12">
                            <p className="overline mb-4">Everything you need to</p>
                            <h2 className="font-display font-black uppercase leading-[0.95] tracking-tightest text-4xl sm:text-5xl">
                                Create, Release and <span className="text-brand-blue">Grow</span>
                            </h2>
                            <p className="mt-5 text-neutral-500 leading-relaxed">
                                From the first idea to the final release and beyond, we stay with you at every stage of your journey.
                            </p>
                            <Link to="/services" className="mt-6 inline-flex items-center gap-1.5 link-blue uppercase text-sm tracking-wide" data-testid="home-view-services">
                                View all services <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-l border-t border-[#e8e1d4]">
                            {services.map((s, i) => (
                                <Reveal key={s.slug} delay={i * 0.05}>
                                    <Link
                                        to={`/services`}
                                        data-testid={`home-service-${s.slug}`}
                                        className="group block p-6 border-t border-l border-[#e8e1d4] h-full text-center hover:bg-cream transition-colors card-lift"
                                    >
                                        <div className="flex items-center justify-center gap-4">
                                            <span className="big-index text-2xl text-neutral-300 group-hover:text-brand-yellow transition-colors">{s.number}</span>
                                            <Icon name={s.icon} className="h-5 w-5 text-neutral-400 group-hover:text-ink transition-colors" />
                                        </div>
                                        <h3 className="mt-8 font-display font-bold uppercase tracking-tight text-lg">{s.title}</h3>
                                        <p className="mt-1.5 text-xs text-neutral-500 leading-relaxed">{s.tagline}</p>
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured work */}
            <section className="py-16 lg:py-24 bg-navy text-white grain" data-testid="home-featured">
                <div className="mx-container">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <p className="overline !text-brand-yellow mb-3">Featured Work</p>
                        <h2 className="font-display font-black uppercase tracking-tightest text-4xl sm:text-5xl leading-none">The work speaks first</h2>
                        <Link to="/portfolio" className="mt-4 inline-flex items-center gap-1.5 text-brand-yellow uppercase text-sm font-semibold tracking-wide" data-testid="home-see-work">
                            See the work <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {portfolio.slice(0, 4).map((p, i) => {
                            const ext = !!p.link_url;
                            const Wrap = ext ? "a" : Link;
                            const wp = ext ? { href: p.link_url, target: "_blank", rel: "noreferrer" } : { to: "/portfolio" };
                            return (
                            <Reveal key={p.slug} delay={i * 0.06}>
                                <Wrap {...wp} className="group block card-lift" data-testid={`home-work-${p.slug}`}>
                                    <div className="relative overflow-hidden aspect-[3/4]">
                                        <BlurImage src={p.cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                        <div className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/15 backdrop-blur grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Play className="h-4 w-4 text-white" />
                                        </div>
                                    </div>
                                    <h3 className="mt-3 font-display font-bold uppercase tracking-tight text-sm text-center">{p.title}</h3>
                                    <p className="text-xs text-white/50 text-center">{p.category}</p>
                                </Wrap>
                            </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Journey */}
            <section className="py-16 lg:py-24 bg-offwhite" data-testid="home-journey">
                <div className="mx-container">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <p className="overline mb-3">Your Journey. Our Mission.</p>
                        <h2 className="font-display font-black uppercase tracking-tightest text-4xl sm:text-5xl leading-none">Find your stage</h2>
                        <p className="mt-4 text-neutral-500">Wherever you are, there's a next step. We meet you there and take you forward.</p>
                    </div>
                    <div className="relative grid md:grid-cols-4 gap-8">
                        <div className="hidden md:block absolute top-7 left-[12%] right-[12%] h-px bg-neutral-300" />
                        {journey.map((j, i) => (
                            <Reveal key={j.number} delay={i * 0.08}>
                                <div className="relative text-center md:text-left" data-testid={`home-journey-${j.number}`}>
                                    <div className="relative z-10 mx-auto md:mx-0 h-14 w-14 rounded-full bg-white border border-neutral-200 grid place-items-center">
                                        <Icon name={j.icon} className={`h-5 w-5 ${i % 2 === 0 ? "text-brand-yellow" : "text-brand-blue"}`} />
                                    </div>
                                    <span className="big-index block mt-6 text-3xl text-neutral-300">{j.number}</span>
                                    <h3 className="mt-2 font-display font-bold uppercase tracking-tight">{j.stage}</h3>
                                    <p className="mt-2 text-sm text-neutral-500 leading-relaxed">{j.headline} {j.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <div className="mt-14 text-center">
                        <Link to="/signup" className="btn-dark" data-testid="home-find-stage">Find your stage <ArrowRight className="h-4 w-4" /></Link>
                    </div>
                </div>
            </section>

            {/* Courses */}
            <section className="py-16 lg:py-24 bg-white" data-testid="home-courses">
                <div className="mx-container">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <p className="overline mb-3">Education</p>
                        <h2 className="font-display font-black uppercase tracking-tightest text-4xl sm:text-5xl leading-[0.95]">
                            Learn the business behind the art.
                        </h2>
                        <Link to="/courses" className="mt-4 inline-flex items-center gap-1.5 link-blue uppercase text-sm tracking-wide" data-testid="home-browse-courses">
                            Browse all courses <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {courses.slice(0, 3).map((c, i) => (
                            <Reveal key={c.slug} delay={i * 0.06}>
                                <Link to={c.landing || `/courses/${c.slug}`} className="group block card-lift" data-testid={`home-course-${c.slug}`}>
                                    <div className="relative overflow-hidden aspect-[16/10]">
                                        <BlurImage src={c.cover} alt={c.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        {c.badge && <span className="absolute top-3 left-3 bg-brand-yellow text-ink text-[10px] font-bold uppercase tracking-wide px-2.5 py-1">{c.badge}</span>}
                                    </div>
                                    <div className="pt-4 pb-6 px-4 text-center">
                                        <div className="flex items-center justify-center gap-2 text-xs text-neutral-500">
                                            {c.coming_soon ? <span className="uppercase tracking-wide font-semibold">Coming soon</span> : (Number(c.reviews) > 0 ? <><Star className="h-3.5 w-3.5 fill-brand-yellow text-brand-yellow" /> {c.rating}</> : null)}
                                            <span>·</span><span>{c.level}</span>
                                        </div>
                                        <h3 className="mt-2 font-display font-bold text-lg leading-tight group-hover:text-brand-blue transition-colors">{c.title}</h3>
                                        {c.coming_soon ? (
                                            <button onClick={(e) => { e.preventDefault(); openNotify({ kind: "course", slug: c.slug, title: c.title }); }} className="mt-3 btn-dark !py-2 !px-4 text-[11px]" data-testid={`home-course-notify-${c.slug}`}>Notify Me</button>
                                        ) : (
                                            <p className="mt-2 font-display font-bold">₹{c.price.toLocaleString("en-IN")} {Number(c.original_price) > Number(c.price) && <span className="text-neutral-400 line-through font-normal text-sm">₹{Number(c.original_price).toLocaleString("en-IN")}</span>}</p>
                                        )}
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trusted by */}
            <section className="py-12 border-y border-[#e8e1d4] bg-offwhite overflow-hidden" data-testid="home-trusted">
                <div className="mx-container">
                    <p className="overline mb-6">Trusted by amazing artists</p>
                </div>
                <div className="marquee-mask">
                    <div className="flex items-center gap-14 w-max animate-marquee pl-14">
                        {[...artists, ...artists, "Many more"].map((a, i) => (
                            <span key={i} className={`font-display font-semibold uppercase tracking-tight text-2xl whitespace-nowrap ${a === "Many more" ? "italic text-brand-gold normal-case" : "text-neutral-400"}`}>{a}</span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Artist Launch flagship */}
            <section className="py-16 lg:py-24 bg-espresso text-white grain" data-testid="home-flagship">
                <div className="mx-container grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="overline !text-brand-gold mb-3">Flagship offer</p>
                        <h2 className="font-display font-semibold text-4xl sm:text-5xl leading-[1.0]">The MUVIARTS <span className="italic text-brand-gold">Artist Launch.</span></h2>
                        <p className="mt-5 text-white/60 max-w-lg leading-relaxed">Production, visuals, branding and marketing in one complete package to release your music professionally, configured to your goals, budget and timeline.</p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <button onClick={() => openLead?.("Artist Launch")} className="btn-primary" data-testid="home-launch-cta">Start My Artist Launch <ArrowRight className="h-4 w-4" /></button>
                            <Link to="/launch" className="btn-ghost-light !px-2 uppercase text-sm font-semibold tracking-wide">See what's included <ArrowRight className="h-4 w-4" /></Link>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {["Production", "Visuals", "Branding", "Marketing"].map((t, i) => (
                            <div key={t} className="border border-white/10 p-6 bg-white/5"><span className="big-index text-3xl text-white/25">0{i + 1}</span><p className="mt-3 font-display font-semibold">{t}</p></div>
                        ))}
                    </div>
                </div>
            </section>

            {tests.length > 0 && <Testimonials tests={tests} />}

            {/* CTA */}
            <section className="py-16 lg:py-24 bg-white" data-testid="home-cta">
                <div className="mx-container">
                    <div className="bg-cream border border-[#e8e1d4] px-8 sm:px-14 py-16 lg:py-24 relative overflow-hidden text-center">
                        <div className="max-w-2xl mx-auto relative z-10">
                            <h2 className="font-display font-semibold tracking-tight text-4xl sm:text-6xl leading-[0.95]">
                                Ready to build something <span className="italic text-brand-gold">that lasts?</span>
                            </h2>
                            <p className="mt-6 text-neutral-500 text-lg max-w-lg mx-auto">Start your artist journey with MUVIARTS today, or book a free consultation with our team.</p>
                            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                                <button onClick={() => openLead?.("General")} className="btn-primary" data-testid="cta-start">Start Your Project <ArrowRight className="h-4 w-4" /></button>
                                <button onClick={() => openWhatsApp?.("launch")} className="btn-outline" data-testid="cta-consult">Chat on WhatsApp <ArrowRight className="h-4 w-4" /></button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
